import { useState } from 'react';
import { Announcement } from '@servicetitan/design-system';
import { useNavigate } from 'react-router-dom';
import {
  useApolloClient,
  useQuery,
  useSubscription,
} from '@apollo/client/react';
import paths from '@app/routes/paths';
import usePermissions from '@app/permissions/usePermissions';
import { permissions } from '@app/permissions/permissions';
import { ConfigReviewStatus } from '@api/generated/graphql';
import { CONFIG_REVIEW_BANNER_STATE_QUERY } from '@pages/settings/ConfigReviewPage/queries/bannerStateQuery';
import { configReviewBannerChangedSubscription } from './subscriptions/configReviewBannerChanged';
import { configReviewStrings } from '@pages/settings/ConfigReviewPage/strings';
import { STORAGE_KEY_CONFIG_REVIEW_BANNER_DISMISSED } from '@constants/storage';

type BannerSeverity = 'critical' | 'warning';

// The dismissal is persisted in sessionStorage so closing the banner sticks across page reloads but
// resets on the next login (fresh session = empty sessionStorage). We store the severity dismissed at
// rather than a boolean so we can re-show only on an escalation to critical (see shouldShow below).
const readDismissedSeverity = (): BannerSeverity | null => {
  const value = sessionStorage.getItem(
    STORAGE_KEY_CONFIG_REVIEW_BANNER_DISMISSED,
  );
  return value === 'critical' || value === 'warning' ? value : null;
};

const ConfigurationReviewBanner = () => {
  const client = useApolloClient();
  const navigate = useNavigate();
  const [dismissedSeverity, setDismissedSeverity] =
    useState<BannerSeverity | null>(readDismissedSeverity);
  const canView = usePermissions(
    [permissions.admin.CONFIGURATION_REVIEW.VIEW],
    'all',
  );

  const { data } = useQuery(CONFIG_REVIEW_BANNER_STATE_QUERY, {
    skip: !canView,
  });

  // Realtime Pass<->Fail transitions arrive over the subscription (server-scoped to the user's authorized
  // tenants). The payload is the full banner state, so write it straight into the query cache; the
  // show/hide decision (including any escalation re-show) is derived from that state at render time.
  useSubscription(configReviewBannerChangedSubscription, {
    skip: !canView,
    onData: ({ data: subscriptionData }) => {
      const changed = subscriptionData.data?.configReviewBannerChanged;
      if (!changed) return;

      client.writeQuery({
        query: CONFIG_REVIEW_BANNER_STATE_QUERY,
        data: { configReviewBanner: changed },
      });
    },
  });

  const tenantBanners = data?.configReviewBanner?.tenantBanners ?? [];

  // The banner is the worst-of the admin status across the tenants the user is authorized for (the server
  // already scoped tenantBanners to them): any NeedAction is critical, any NeedReview is a warning.
  const hasAction = tenantBanners.some(
    (t) => t.adminReviewStatus === ConfigReviewStatus.NeedAction,
  );
  const hasReview = tenantBanners.some(
    (t) => t.adminReviewStatus === ConfigReviewStatus.NeedReview,
  );

  let severity: BannerSeverity | null = null;
  if (hasAction) {
    severity = 'critical';
  } else if (hasReview) {
    severity = 'warning';
  }

  const handleClose = () => {
    if (!severity) return;
    sessionStorage.setItem(
      STORAGE_KEY_CONFIG_REVIEW_BANNER_DISMISSED,
      severity,
    );
    setDismissedSeverity(severity);
  };

  // Once dismissed, the banner stays hidden for the rest of the session, with one exception: a realtime
  // transition that escalates a previously-dismissed warning into a critical (NeedAction) re-shows it, so
  // the worst-case regression is never silently hidden. Dismissing that critical then keeps it hidden.
  const isEscalationToCritical =
    dismissedSeverity === 'warning' && severity === 'critical';
  const isDismissed = dismissedSeverity !== null && !isEscalationToCritical;

  if (!canView || !severity || isDismissed) {
    return null;
  }

  return (
    <Announcement
      status={severity}
      title={configReviewStrings.generalBanner}
      actionName='Review Configuration'
      onActionClick={() => navigate(`/${paths.configReview}`)}
      onClose={handleClose}
      data-testid='config-review-banner'
    />
  );
};

export default ConfigurationReviewBanner;
